/**
 * Set-selection feature module (Spec B, Task 1).
 *
 * Inbound: `setSetLibrary` (Unity's answer to `requestSetLibrary`, the catalog of built-in +
 * custom sets available for the coverage/Suggest-Shots set-picker) and `applySetApplied` (the
 * ack for `applySet`, after Unity has imported the chosen set-variation into the scene).
 *
 * Outbound: `requestSetLibrary` / `applySet` are fire-and-await senders — each arms a
 * single-flight waiter, sends, and resolves/rejects when the matching inbound verb lands (or
 * times out). Modeled on `features/blocking.ts`'s coverageApplied waiter.
 *
 * Set-variation identifiers (`SetLibraryEntry.id` / `setVariationIdentifier`) are EPHEMERAL —
 * a per-collection static counter on the Unity side, not a stable id. Never persist them.
 */

import { enqueueSendToNative } from '../transport';
import type { HandlerMap } from '../dispatch';

export interface SetLibraryEntry {
  id: number;         // setVariationIdentifier — EPHEMERAL (per-collection static counter). Never persist.
  label: string;      // human label from Unity GetDefaultSetAndVariationName
  setName: string;    // localized set name (without variation suffix)
  section: 'builtIn' | 'custom';
}

/** Scene-level selection resolved from the LLM pick or the user's dropdown choice. */
export type SetSelection =
  | { kind: 'none' }
  | { kind: 'background'; backgroundPrompt?: string }
  | { kind: 'set'; setVariationIdentifier: number; label: string };

const SET_LIBRARY_TIMEOUT_MS = 20000;
const APPLY_SET_TIMEOUT_MS = 30000; // set import can touch many rows across all shots

// The save runs Unity's full set export: it copies the project and renders one preview per shot,
// and only acks on completion. 45s is the agreed ceiling (plan D8 as amended).
const SAVE_SCENE_AS_SET_TIMEOUT_MS = 45000;
const DELETE_CUSTOM_SET_TIMEOUT_MS = 15000;
// Previews are decorative: on timeout the dialog just keeps its placeholder, so this is short.
const SET_PREVIEWS_TIMEOUT_MS = 10000;

// ── Inbound message shapes (match Unity outbound JSON) ──────────────────────
export interface SetSetLibraryMessage { type: 'setSetLibrary'; payload?: { entries?: SetLibraryEntry[] } }
export interface ApplySetAppliedMessage { type: 'applySetApplied'; payload?: { sceneId?: number; ok?: boolean; error?: string } }
export interface SceneSavedAsSetMessage { type: 'sceneSavedAsSet'; payload?: { sceneId?: number; ok?: boolean; error?: string } }
export interface CustomSetDeletedMessage { type: 'customSetDeleted'; payload?: { setVariationIdentifier?: number; ok?: boolean; error?: string } }
export interface SetSetPreviewsMessage { type: 'setSetPreviews'; payload?: { setVariationIdentifier?: number; previewUrls?: string[] } }

export type SetsMessage =
  | SetSetLibraryMessage
  | ApplySetAppliedMessage
  | SceneSavedAsSetMessage
  | CustomSetDeletedMessage
  | SetSetPreviewsMessage;

/**
 * Error keys Unity can return, as implemented in slice 1. `timeout` and `superseded` are added
 * web-side by the waiters below and never come off the wire.
 */
export type SaveSceneAsSetError = 'bad-request' | 'busy' | 'duplicate' | 'save-failed' | 'timeout' | 'superseded';
export type DeleteCustomSetError = 'set-not-found' | 'not-custom' | 'delete-failed' | 'timeout' | 'superseded';

// ── Single-flight waiters (one coverage flow at a time) ─────────────────────
let libraryWaiter: { resolve: (v: SetLibraryEntry[]) => void; reject: (e: unknown) => void; timer: ReturnType<typeof setTimeout> } | null = null;
let applySetWaiter: { resolve: (v: { ok: boolean; error?: string }) => void; timer: ReturnType<typeof setTimeout> } | null = null;
let saveSceneAsSetWaiter: { resolve: (v: { ok: boolean; error?: SaveSceneAsSetError }) => void; timer: ReturnType<typeof setTimeout> } | null = null;
let deleteCustomSetWaiter: { resolve: (v: { ok: boolean; error?: DeleteCustomSetError }) => void; timer: ReturnType<typeof setTimeout> } | null = null;
let setPreviewsWaiter: { id: number; resolve: (v: string[]) => void; timer: ReturnType<typeof setTimeout> } | null = null;

export function sendRequestSetLibrary(timeoutMs: number = SET_LIBRARY_TIMEOUT_MS): Promise<SetLibraryEntry[]> {
  return new Promise<SetLibraryEntry[]>((resolve, reject) => {
    if (libraryWaiter) { clearTimeout(libraryWaiter.timer); libraryWaiter.reject(new Error('[sets] superseded')); }
    const timer = setTimeout(() => { libraryWaiter = null; reject(new Error('[sets] setSetLibrary timed out')); }, timeoutMs);
    libraryWaiter = { resolve, reject, timer };
    enqueueSendToNative('requestSetLibrary', {});
  });
}

export function sendApplySet(
  args: { sceneId: number; setVariationIdentifier: number },
  timeoutMs: number = APPLY_SET_TIMEOUT_MS,
): Promise<{ ok: boolean; error?: string }> {
  return new Promise<{ ok: boolean; error?: string }>((resolve) => {
    if (applySetWaiter) { clearTimeout(applySetWaiter.timer); applySetWaiter.resolve({ ok: false, error: 'superseded' }); }
    const timer = setTimeout(() => { applySetWaiter = null; resolve({ ok: false, error: 'timeout' }); }, timeoutMs);
    applySetWaiter = { resolve, timer };
    enqueueSendToNative('applySet', { sceneId: args.sceneId, setVariationIdentifier: args.setVariationIdentifier });
  });
}

/**
 * Save the current scene as a custom set. Resolves (never rejects) so callers can branch on
 * `error` uniformly — matching sendApplySet.
 *
 * IMPORTANT for callers: after ANY ack (ok or not) every cached setVariationIdentifier is stale.
 * Unity's export ends in a full re-collect that reassigns every id, so refetch the library and drop
 * any preview cache before the next applySet / deleteCustomSet / requestSetPreviews.
 */
export function sendSaveSceneAsSet(
  args: { sceneId: number; setName: string; overwrite: boolean },
  timeoutMs: number = SAVE_SCENE_AS_SET_TIMEOUT_MS,
): Promise<{ ok: boolean; error?: SaveSceneAsSetError }> {
  return new Promise<{ ok: boolean; error?: SaveSceneAsSetError }>((resolve) => {
    if (saveSceneAsSetWaiter) { clearTimeout(saveSceneAsSetWaiter.timer); saveSceneAsSetWaiter.resolve({ ok: false, error: 'superseded' }); }
    const timer = setTimeout(() => { saveSceneAsSetWaiter = null; resolve({ ok: false, error: 'timeout' }); }, timeoutMs);
    saveSceneAsSetWaiter = { resolve, timer };
    enqueueSendToNative('saveSceneAsSet', { sceneId: args.sceneId, setName: args.setName, overwrite: args.overwrite });
  });
}

/**
 * Delete a custom set by variation identifier. Same staleness rule as the save: Unity re-collects
 * after removing the row, so every remaining id is reassigned — refetch before using any id again.
 *
 * Note on `set-not-found`: it means "this id did not resolve", which covers a malformed payload AND
 * an id that simply went stale. It is never proof the set is gone — treat it as "refresh and let the
 * user retry".
 */
export function sendDeleteCustomSet(
  args: { setVariationIdentifier: number },
  timeoutMs: number = DELETE_CUSTOM_SET_TIMEOUT_MS,
): Promise<{ ok: boolean; error?: DeleteCustomSetError }> {
  return new Promise<{ ok: boolean; error?: DeleteCustomSetError }>((resolve) => {
    if (deleteCustomSetWaiter) { clearTimeout(deleteCustomSetWaiter.timer); deleteCustomSetWaiter.resolve({ ok: false, error: 'superseded' }); }
    const timer = setTimeout(() => { deleteCustomSetWaiter = null; resolve({ ok: false, error: 'timeout' }); }, timeoutMs);
    deleteCustomSetWaiter = { resolve, timer };
    enqueueSendToNative('deleteCustomSet', { setVariationIdentifier: args.setVariationIdentifier });
  });
}

/**
 * Fetch preview images for one set variation (≤6 base64 JPEG data URLs, Unity-capped).
 *
 * Resolves `[]` rather than rejecting on every failure path — an empty list is a legitimate answer
 * (older sets saved before previews existed, a missing file, or no rows), so the caller shows a
 * placeholder and never surfaces an error.
 */
export function sendRequestSetPreviews(
  setVariationIdentifier: number,
  timeoutMs: number = SET_PREVIEWS_TIMEOUT_MS,
): Promise<string[]> {
  return new Promise<string[]>((resolve) => {
    if (setPreviewsWaiter) { clearTimeout(setPreviewsWaiter.timer); setPreviewsWaiter.resolve([]); }
    const timer = setTimeout(() => { setPreviewsWaiter = null; resolve([]); }, timeoutMs);
    setPreviewsWaiter = { id: setVariationIdentifier, resolve, timer };
    enqueueSendToNative('requestSetPreviews', { setVariationIdentifier });
  });
}

export const setsHandlers = {
  setSetLibrary: (msg: SetSetLibraryMessage) => {
    const entries = Array.isArray(msg.payload?.entries) ? msg.payload!.entries! : [];
    if (libraryWaiter) { clearTimeout(libraryWaiter.timer); libraryWaiter.resolve(entries); libraryWaiter = null; }
  },
  applySetApplied: (msg: ApplySetAppliedMessage) => {
    const ok = !!msg.payload?.ok;
    const error = typeof msg.payload?.error === 'string' ? msg.payload!.error : undefined;
    if (applySetWaiter) { clearTimeout(applySetWaiter.timer); applySetWaiter.resolve({ ok, error }); applySetWaiter = null; }
  },
  sceneSavedAsSet: (msg: SceneSavedAsSetMessage) => {
    const ok = !!msg.payload?.ok;
    // The wire is untyped; narrow to the documented key set so callers get compiler-checked
    // comparisons. An unrecognised key would still flow through as-is and hit the generic branch.
    const error = typeof msg.payload?.error === 'string' ? (msg.payload!.error as SaveSceneAsSetError) : undefined;
    if (saveSceneAsSetWaiter) { clearTimeout(saveSceneAsSetWaiter.timer); saveSceneAsSetWaiter.resolve({ ok, error }); saveSceneAsSetWaiter = null; }
  },
  customSetDeleted: (msg: CustomSetDeletedMessage) => {
    const ok = !!msg.payload?.ok;
    const error = typeof msg.payload?.error === 'string' ? (msg.payload!.error as DeleteCustomSetError) : undefined;
    if (deleteCustomSetWaiter) { clearTimeout(deleteCustomSetWaiter.timer); deleteCustomSetWaiter.resolve({ ok, error }); deleteCustomSetWaiter = null; }
  },
  setSetPreviews: (msg: SetSetPreviewsMessage) => {
    if (!setPreviewsWaiter) return;
    // Only resolve the request this reply is for, and require the id to be PRESENT and numeric to
    // say so. Ids are ephemeral and the dialog caches by id, so applying a reply that cannot be
    // proven to belong to the outstanding request risks caching one set's images against another.
    // Anything unprovable is dropped; the request then falls through to its timeout, which resolves
    // [] — a placeholder, not an error. The cost of being wrong here is a 10s placeholder in a
    // case Unity should never produce; the cost of being wrong the other way is a wrong image.
    if (typeof msg.payload?.setVariationIdentifier !== 'number'
        || msg.payload.setVariationIdentifier !== setPreviewsWaiter.id) return;
    const urls = Array.isArray(msg.payload?.previewUrls)
      ? msg.payload!.previewUrls!.filter((u): u is string => typeof u === 'string')
      : [];
    clearTimeout(setPreviewsWaiter.timer);
    setPreviewsWaiter.resolve(urls);
    setPreviewsWaiter = null;
  },
} satisfies HandlerMap;
